import type { ReactNode } from "react";
import { ArrowRight, Bath, HardHat, Utensils, Waves } from "lucide-react";
import { CanonicalLink as Link } from "@/components/CanonicalLink";
import { getService } from "@/data/services";
import { additionalServiceIntroductions } from "@/data/additional-service-introductions";

const slugs = ["drainage", "renovation-cuisine", "renovation-salle-de-bain", "entrepreneur-general"] as const;

const icons: Record<string, ReactNode> = {
  drainage: <Waves className="h-5 w-5" aria-hidden="true" />,
  "renovation-cuisine": <Utensils className="h-5 w-5" aria-hidden="true" />,
  "renovation-salle-de-bain": <Bath className="h-5 w-5" aria-hidden="true" />,
  "entrepreneur-general": <HardHat className="h-5 w-5" aria-hidden="true" />,
};

export function AdditionalServices({ citySlug, editorial = false }: { citySlug?: string; editorial?: boolean } = {}) {
  const items = slugs.map((slug) => {
    const service = getService(slug);
    if (!service) throw new Error(`Missing additional service: ${slug}`);
    return { service, introduction: additionalServiceIntroductions[slug] };
  });

  return (
    <div className="mt-10 border-t border-border pt-8" data-testid="section-additional-services">
      <div className="mb-5 flex flex-col items-center gap-1 text-center">
        <h2 id="additional-services-heading" className="text-lg md:text-xl font-bold text-primary">
          Drainage et projets de rénovation
        </h2>
        <p className="text-sm text-muted-foreground">Nous réalisons aussi ces travaux.</p>
      </div>
      <ul aria-labelledby="additional-services-heading" className="mx-auto grid max-w-4xl gap-3 sm:grid-cols-2">
        {items.map(({ service, introduction }) => (
          <li key={service.slug}>
            {editorial ? (
              <article className="h-full rounded-xl border border-border bg-white/60 p-4">
                <div className="mb-3 flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/5 text-primary">
                    {icons[service.slug]}
                  </span>
                  <h3 className="font-semibold text-primary">{service.title}</h3>
                </div>
                <p
                  className="text-sm leading-relaxed text-muted-foreground"
                  data-testid={`paragraph-editorial-service-${service.slug}`}
                >
                  {introduction.before}{" "}
                  <Link
                    href={`/services/${service.slug}${citySlug ? `/${citySlug}` : ""}`}
                    data-testid={`link-editorial-service-${service.slug}`}
                    className="font-medium text-primary underline decoration-primary/35 underline-offset-4 transition-colors hover:text-accent hover:decoration-accent focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                  >
                    {introduction.anchor}
                  </Link>
                  {introduction.after}
                </p>
              </article>
            ) : (
              <Link
                href={`/services/${service.slug}${citySlug ? `/${citySlug}` : ""}`}
                data-testid={`link-additional-service-${service.slug}`}
                className="group flex h-full items-center gap-4 rounded-xl border border-border bg-white/60 p-4 transition-colors duration-200 hover:border-accent/50 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/5 text-primary transition-colors duration-200 group-hover:bg-accent/10 group-hover:text-accent">
                  {icons[service.slug]}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-primary">{service.title}</span>
                  <span className="mt-0.5 block text-sm leading-snug text-muted-foreground">
                    {service.summary || service.description}
                  </span>
                </span>
                <ArrowRight
                  className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1 group-hover:text-accent"
                  aria-hidden="true"
                />
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
